/**
 * WhatsApp buttons open WhatsApp itself, never WhatsApp's grey "Continue to chat" page (Gary, 31 Oct 2026).
 *
 * Every wa.me or api.whatsapp.com/send link, whether an <a> or a window.open, is caught here once for the
 * whole app, so no button needs changing:
 *   phone:    the WhatsApp app; if it has not opened in 1.5 seconds, the original link.
 *   computer: WhatsApp Web on that chat (or its "send to" list) in a new tab, which skips the grey page.
 * The public sites do the same (apps/web WhatsAppDirect.astro), trying the desktop app first; here a
 * computer goes straight to WhatsApp Web so the Portal tab is never navigated away.
 */

function parse(href: string): { phone: string; text: string } | null {
  let url: URL;
  try { url = new URL(href, window.location.href); } catch { return null; }
  const isWaMe = url.hostname === 'wa.me';
  if (!isWaMe && !(url.hostname === 'api.whatsapp.com' && url.pathname === '/send')) return null;
  const phone = (isWaMe ? url.pathname : url.searchParams.get('phone') ?? '').replace(/[^0-9]/g, '');
  const text = url.searchParams.get('text') ?? '';
  if (!phone && !text) return null;
  return { phone, text };
}

function isPhone(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function queryFor(chat: { phone: string; text: string }): string {
  return [chat.phone ? `phone=${chat.phone}` : '', chat.text ? `text=${encodeURIComponent(chat.text)}` : '']
    .filter(Boolean).join('&');
}

const nativeOpen = window.open.bind(window);

function openOnPhone(href: string, query: string): void {
  let opened = false;
  const markOpened = (): void => { opened = true; };
  const onHidden = (): void => { if (document.hidden) opened = true; };
  window.addEventListener('blur', markOpened);
  document.addEventListener('visibilitychange', onHidden);
  window.setTimeout(() => {
    window.removeEventListener('blur', markOpened);
    document.removeEventListener('visibilitychange', onHidden);
    if (!opened) window.location.href = href;
  }, 1500);
  window.location.href = `whatsapp://send?${query}`;
}

/** Opens `href` the direct way when it is a WhatsApp link; false when it is not one. */
function openWhatsApp(href: string): boolean {
  const chat = parse(href);
  if (!chat) return false;
  const query = queryFor(chat);
  if (isPhone()) openOnPhone(href, query);
  else nativeOpen(`https://web.whatsapp.com/send?${query}`, '_blank', 'noopener');
  return true;
}

export function installWhatsAppDirect(): void {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = e.target instanceof Element
      ? e.target.closest('a[href^="https://wa.me/"], a[href^="https://api.whatsapp.com/send"]')
      : null;
    if (!(target instanceof HTMLAnchorElement)) return;
    if (openWhatsApp(target.href)) e.preventDefault();
  });

  window.open = (url?: string | URL, target?: string, features?: string): Window | null => {
    if (url !== undefined && openWhatsApp(String(url))) return null;
    return nativeOpen(url, target, features);
  };
}
